import { Color } from '@persona-id/design-tokens';
import ToColor from 'color';

import {
  ColorRule,
  DatumType,
  DatumTypeToken,
  Directionality,
  Graph,
  GraphAttributes,
  GraphRelationships,
  HashableNodeId,
  Node,
  NodeDataTypeIcon,
  NodeId,
  nodeIdToHashable,
  NodeRenderConfig,
  NodeState,
  PropertyConfig,
  PropertySource,
  V2Data,
} from 'dashboard/models/graph';
import { ServerNodeRenderConfig } from 'dashboard/models/graph-backend';
import {
  HydratedAccount,
  HydratedAccountSimilaritySelfie,
  HydratedNode,
  LightlyHydratedNode,
} from 'dashboard/models/graph-hydration';
import {
  ACCOUNT_DATUM_TYPE,
  BIRTHDATE_DATUM_TYPE,
  BROWSER_FINGERPRINT_DATUM_TYPE,
  DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE,
  EMAIL_DATUM_TYPE,
  GOVERNMENT_ID_DATUM_TYPE,
  ID_NUMBER_DATUM_TYPE,
  NETWORK_DATUM_TYPE,
  PHONE_NUMBER_DATUM_TYPE,
  POSTAL_CODE_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_DATUM_TYPE,
  SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE,
} from 'lib/constants/graph';
import { ApiModel } from 'lib/interfaces/api';

const accountNodeId1: NodeId = {
  token: ACCOUNT_DATUM_TYPE.token,
  value: 'act_AjwTD9vqHuPJFA8YWAP1qzsZ',
};
const accountNodeId2: NodeId = {
  token: ACCOUNT_DATUM_TYPE.token,
  value: 'act_gtF6fLX5hdwusGsjvd3oLNup',
};
const accountNodeId3: NodeId = {
  token: ACCOUNT_DATUM_TYPE.token,
  value: 'act_gKhJyuU2BjSrrXQC3cLtvxV7',
};
const idNumberNodeId: NodeId = {
  token: GOVERNMENT_ID_DATUM_TYPE.token,
  value: 'ppbd_Rcxpnedbj1sZYj4R365yBt4x',
};
const networkNodeId: NodeId = {
  token: NETWORK_DATUM_TYPE.token,
  value: 'net_123',
};

const mockNodeRenderConfig: ServerNodeRenderConfig[] = [
  {
    'datum-type-token': 'gdt_uu9GtMYVEJTzBKfy8tyLKh1L',
    'display-name': 'Account',
    color: '#631fff',
    icon: 'account' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. act_zVbXMCv5oJioB4zkygCWetbT',
    'context-menu': [
      {
        source: 'persona' as PropertySource,
        value: 'name',
      },
      {
        source: 'persona' as PropertySource,
        value: 'reference_id',
      },
      {
        source: 'persona' as PropertySource,
        value: 'inquiry_breakdown',
      },
      {
        source: 'persona' as PropertySource,
        value: 'status',
      },
      {
        source: 'persona' as PropertySource,
        value: 'tags',
      },
      {
        source: 'persona' as PropertySource,
        value: 'created_at',
      },
    ],
  },
  {
    'datum-type-token': 'gdt_WzrShjScrpZhaWtt8pA2z3PN',
    'display-name': 'Selfie Similarity',
    color: '#3DD6D6',
    icon: 'smile' as NodeDataTypeIcon,
    'search-placeholder': '',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_n4diQphbpCt4zGCzxUPzFiyX',
    'display-name': 'Address',
    color: '#FABEB2',
    icon: 'address' as NodeDataTypeIcon,
    'search-placeholder': '',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_EB3rN7VhPTiZrSkPAmxsm2gq',
    'display-name': 'Birthdate',
    color: '#A3E1BC',
    icon: 'calendar' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 1970-01-27',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_AFG3bweXWevEPV85HDzpEx9E',
    'display-name': 'Browser Fingerprint',
    color: '#87EAEA',
    icon: 'fingerprint' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. a07cc 1f7e7abc6515fa932b4cdf12b11',
    'context-menu': [
      {
        source: 'persona' as PropertySource,
        value: 'browser_name',
      },
    ],
  },
  {
    'datum-type-token': 'gdt_WV7mWfBrrr51UkBZfmFmobLU',
    'display-name': 'Device Fingerprint',
    color: '#1BB8B8',
    icon: 'fingerprint' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. PTW93ZArKL8c3v4hVUMV',
    'context-menu': [
      {
        source: 'persona' as PropertySource,
        value: 'device_type',
      },
    ],
  },
  {
    'datum-type-token': 'gdt_45ExYQBM1eUr4epDsevS57wR',
    'display-name': 'Device Vendor ID',
    color: '#7CD49F',
    icon: 'fingerprint' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 7bab59c4-6be9-4bc9-ba8f-e3f8a130701f',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_4MQyZyGD4L1vuyg2TBtPMNTg',
    'display-name': 'Email Address',
    color: '#FFDE85',
    icon: 'email' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. username@; @example.com; username@example.com',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_yPSPkno8tarT8DHtPGfMcPnK',
    'display-name': 'Government ID Number',
    color: '#FABEB2',
    icon: 'inquiries' as NodeDataTypeIcon,
    'search-placeholder': '',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_jzwzwhJ2VDUKhF7MPdVzvek2',
    'display-name': 'Name',
    color: '#51E3E3',
    icon: 'name' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. John; Smith; John Smith',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_ZkmBSabwLQiukRyoJ6EcGmH3',
    'display-name': 'IP Address',
    color: '#56C883',
    icon: 'globe' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 127.0.0.1',
    'context-menu': [
      {
        source: 'persona' as PropertySource,
        value: 'threat_level',
      },
      {
        source: 'persona' as PropertySource,
        value: 'proxy_type',
      },
      {
        source: 'persona' as PropertySource,
        value: 'location',
      },
    ],
  },
  {
    'datum-type-token': 'gdt_7c4pQfR4oDrvDAtxKyxUifjG',
    'display-name': 'Phone Number',
    color: '#FEB9CB',
    icon: 'phone' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 1 201 555-1212; +44 7700 900077',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_iwR6qhiftW1svVcnJ5JTCxPy',
    'display-name': 'Postal Code',
    color: '#FFC31F',
    icon: 'postal' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 90210; 90210+1234; H4A 1A1',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_unUMyn9Cs1s6LbpYhxB8sSe6',
    'display-name': 'SSN',
    color: '#5598FC',
    icon: 'inquiries' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 123456789',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_NKdpZJGaGEVEiRX4YLRG4KWv',
    'display-name': 'Last 4 SSN',
    color: '#2379FA',
    icon: 'inquiries' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 6789',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_9BKhJyF9fYRxs2oL5A69gcUo',
    'display-name': 'Government ID Document Number',
    color: '#735A5A',
    icon: 'inquiries' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. 123456789',
    'context-menu': [],
  },
  {
    'datum-type-token': 'gdt_oNULu89rdCnJfVeDf9CKbYF7',
    'display-name': 'Device Token',
    color: '#022150',
    icon: 'fingerprint' as NodeDataTypeIcon,
    'search-placeholder': 'e.g. dev_zVbXMCv5oJioB4zkygCWetbT',
    'context-menu': [],
  },
];

export function createMockGraphAttributes(): GraphAttributes {
  return {
    'min-cluster-size': 6,
    'max-cluster-size': 100,
    'property-filters': [],
    tags: ['FRAUD', 'SCAMMER'],
    'date-range': 90,
    'available-tags': ['FRAUD', 'SCAMMER'],
    'coloring-rules': [],
    'searchable-fields': [],
    'node-render-configs': [],
    'node-functions-configs': [],
    'preset-summaries': [],
    'node-types': [],
    'property-configs': [],
    'default-graph-properties': {},
  };
}

export function createMockGraph(
  attributes?: Partial<GraphAttributes>,
  relationships?: Partial<GraphRelationships>,
  id?: string,
): Graph {
  return {
    id: id ?? 'grph_123',
    type: ApiModel.Graph,
    attributes: {
      ...createMockGraphAttributes(),
      'node-render-configs': mockNodeRenderConfig,
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      organization: { data: null },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      environment: { data: null },
      ...relationships,
    },
  };
}

export function graphConfigurationMockData(): [
  nodeDatumTypes: DatumType[],
  formattedColoringRules: ColorRule[],
  nodeRenderConfigsByToken: Map<DatumTypeToken, NodeRenderConfig>,
  propertyConfigs: PropertyConfig[],
] {
  const nodeRenderConfigsByToken = new Map([
    [
      GOVERNMENT_ID_DATUM_TYPE.token,
      {
        color: ToColor(Color.CorePurple200),
        icon: NodeDataTypeIcon.Government,
        displayName: 'Government ID',
        placeHolder: '',
        contextMenu: [],
      },
    ],
    [
      NETWORK_DATUM_TYPE.token,
      {
        color: ToColor(Color.CorePurple200),
        icon: NodeDataTypeIcon.Globe,
        displayName: 'Government ID',
        placeHolder: '',
        contextMenu: [],
      },
    ],
  ]);

  return [[], [], nodeRenderConfigsByToken, []];
}

export function defaultNodeState(): NodeState {
  return {
    visible: true,
    selected: false,
    highlighted: false,
    dimmed: false,
    loading: false,
    new: false,
    nonfetchedEdgeCount: 0,
    featured: false,
    excluded: false,
  };
}

export function defaultHoverCards(): Set<HTMLDivElement> {
  return new Set();
}

// Graph Explorer V2
export function createMockV2PropertyNodeId(): NodeId {
  return {
    token: ID_NUMBER_DATUM_TYPE.token,
    value: 'F3DK39NW0',
  };
}

export function createMockV2RenderConfig(): NodeRenderConfig {
  return {
    color: ToColor(Color.CorePurple200),
    icon: NodeDataTypeIcon.Government,
    displayName: 'Identification Number',
    placeHolder: '',
    contextMenu: [],
  };
}

export function createMockV2AccountNodeId(): NodeId {
  return {
    token: ACCOUNT_DATUM_TYPE.token,
    value: 'act_AjwTD9vqHuPJFA8YWAP1qzsZ',
  };
}

export function createMockV2PropertyNode(): Node {
  return {
    id: createMockV2PropertyNodeId(),
    state: defaultNodeState(),
    hoverCards: defaultHoverCards(),
  };
}

export function createMockV2AccountNode(): Node {
  return {
    id: createMockV2AccountNodeId(),
    state: defaultNodeState(),
    hoverCards: defaultHoverCards(),
  };
}

export function createMockV2HydratedPropertyNode(): HydratedNode {
  return {
    nodeType: 'id_number',
    hydrationData: {
      id: 'F3DK39NW0',
    },
  };
}

export function createMockV2HydratedAccountNode(): HydratedAccount {
  return {
    nodeType: 'account',
    hydrationData: {
      token: 'act_AjwTD9vqHuPJFA8YWAP1qzsZ',
      status: 'OK',
      'created-at': '2022-09-02T07:34:20.000Z',
      'reference-id': '',
      tags: ['Fraud', 'Suspicious Phone Risk Score'],
      selfies: [],
      name: {
        first: 'First',
        middle: 'Middle',
        last: 'Last',
      },
      'inquiry-counts': {
        approved: 1,
        declined: 0,
        'needs-review': 0,
        completed: 2,
        failed: 0,
        expired: 0,
        created: 0,
        pending: 0,
      },
      'has-completed-inquiries': true,
      properties: [],
      'fields-data': null,
    },
  };
}

export function createMockV2HydratedAccountSimilaritySelfieNode(): HydratedAccountSimilaritySelfie {
  return {
    nodeType: 'account_similarity_selfie',
    hydrationData: {
      account1: {
        id: accountNodeId1.token,
        selfies: [
          {
            id: 'self_123',
            filename: 'cat_burglar.jpg',
          },
        ],
      },
      account2: {
        id: accountNodeId2.token,
        selfies: [
          {
            id: 'self_1234',
            filename: 'cat_robber.jpg',
          },
        ],
      },
      confidence: 99.99,
    },
  };
}

export function createMockV2Data(): V2Data {
  return {
    edges: [
      {
        id1: accountNodeId1,
        id2: idNumberNodeId,
        directionality: Directionality.Bidirectional,
      },
      {
        id1: accountNodeId1,
        id2: networkNodeId,
        directionality: Directionality.Bidirectional,
      },
      {
        id1: accountNodeId3,
        id2: networkNodeId,
        directionality: Directionality.Bidirectional,
      },
      {
        id1: accountNodeId2,
        id2: idNumberNodeId,
        directionality: Directionality.Bidirectional,
      },
    ],
    nodes: [
      {
        id: accountNodeId1,
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: accountNodeId2,
        state: { ...defaultNodeState(), visible: true },
        hoverCards: defaultHoverCards(),
      },
      {
        id: accountNodeId3,
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: idNumberNodeId,
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: networkNodeId,
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
    nonfetchedEdgeCounts: new Map(),
    seedNodes: [
      {
        id: accountNodeId1,
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
  };
}

export function createMockLightHydrationData(): Map<HashableNodeId, LightlyHydratedNode> {
  const lightlyHydratedAccount: LightlyHydratedNode = {
    nodeType: 'account',
    hydrationData: {
      status: 'active',
      tags: [],
      'has-completed-inquiries': false,
    },
  };
  const lightlyHydratedIdNumber: LightlyHydratedNode = {
    nodeType: 'id_number',
    hydrationData: {},
  };
  const lightlyHydratedNetwork: LightlyHydratedNode = {
    nodeType: 'network',
    hydrationData: {},
  };

  return new Map<HashableNodeId, LightlyHydratedNode>([
    [nodeIdToHashable(accountNodeId1), lightlyHydratedAccount],
    [nodeIdToHashable(accountNodeId2), lightlyHydratedAccount],
    [nodeIdToHashable(accountNodeId3), lightlyHydratedAccount],
    [nodeIdToHashable(idNumberNodeId), lightlyHydratedIdNumber],
    [nodeIdToHashable(networkNodeId), lightlyHydratedNetwork],
  ]);
}

export function createMockV2ExpandFromPropertyData(sourcePropertyId: NodeId): V2Data {
  return {
    edges: [
      {
        id1: {
          token: ACCOUNT_DATUM_TYPE.token,
          value: 'act_expanded1',
        },
        id2: sourcePropertyId,
        directionality: Directionality.Bidirectional,
      },
      {
        id1: {
          token: ACCOUNT_DATUM_TYPE.token,
          value: 'act_expanded2',
        },
        id2: sourcePropertyId,
        directionality: Directionality.Bidirectional,
      },
    ],
    nodes: [
      {
        id: {
          token: ACCOUNT_DATUM_TYPE.token,
          value: 'act_expanded1',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: ACCOUNT_DATUM_TYPE.token,
          value: 'act_expanded2',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
    nonfetchedEdgeCounts: new Map(),
    seedNodes: [
      {
        id: {
          token: ACCOUNT_DATUM_TYPE.token,
          value: 'act_expanded1',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
  };
}

export function createMockV2ExpandFromAccountData(sourceAccountId: NodeId): V2Data {
  return {
    nodes: [
      {
        id: {
          token: NETWORK_DATUM_TYPE.token,
          value: 'net_expanded1',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: BIRTHDATE_DATUM_TYPE.token,
          value: 'ppbd_expanded2',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
          value: '123456789',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token,
          value: '1234',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: PHONE_NUMBER_DATUM_TYPE.token,
          value: 'pppn_expanded5',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: EMAIL_DATUM_TYPE.token,
          value: 'ppea_expanded6',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: POSTAL_CODE_DATUM_TYPE.token,
          value: '94102',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: ID_NUMBER_DATUM_TYPE.token,
          value: 'ppid_expanded8',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: BROWSER_FINGERPRINT_DATUM_TYPE.token,
          value: 'f53130f1befa93c840e3fd4c679c4989',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
          value: 'asdfaa1befa93c840e3fd4c679c49sl87',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
      {
        id: {
          token: GOVERNMENT_ID_DATUM_TYPE.token,
          value: 'F3DK39330',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
    edges: [
      {
        id1: sourceAccountId,
        id2: {
          token: NETWORK_DATUM_TYPE.token,
          value: 'net_expanded1',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: BIRTHDATE_DATUM_TYPE.token,
          value: 'ppbd_expanded2',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: SOCIAL_SECURITY_NUMBER_DATUM_TYPE.token,
          value: '123456789',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: SOCIAL_SECURITY_NUMBER_LAST_4_DATUM_TYPE.token,
          value: '1234',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: PHONE_NUMBER_DATUM_TYPE.token,
          value: 'pppn_expanded5',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: EMAIL_DATUM_TYPE.token,
          value: 'ppea_expanded6',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: POSTAL_CODE_DATUM_TYPE.token,
          value: '94102',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: ID_NUMBER_DATUM_TYPE.token,
          value: 'ppid_expanded8',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: BROWSER_FINGERPRINT_DATUM_TYPE.token,
          value: 'f53130f1befa93c840e3fd4c679c4989',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: DEVICE_FINGERPRINT_BROWSER_DATUM_TYPE.token,
          value: 'asdfaa1befa93c840e3fd4c679c49sl87',
        },
        directionality: Directionality.Bidirectional,
      },
      {
        id1: sourceAccountId,
        id2: {
          token: GOVERNMENT_ID_DATUM_TYPE.token,
          value: 'F3DK39330',
        },
        directionality: Directionality.Bidirectional,
      },
    ],
    nonfetchedEdgeCounts: new Map(),
    seedNodes: [
      {
        id: {
          token: NETWORK_DATUM_TYPE.token,
          value: 'net_expanded1',
        },
        state: defaultNodeState(),
        hoverCards: defaultHoverCards(),
      },
    ],
  };
}
